import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowLeft,
  Camera,
  Check,
  Mail,
  Sparkles,
  Upload,
  User,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../../api';
import { useUIStore } from '../../../store';
import { FILE_TYPES } from '../../../utils/constants';
import { resolveUserAvatar } from '../../../utils/media';

const AgentProfilePage = ({ session, sess }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useUIStore();
  const user = session || sess || {};

  const [displayName, setDisplayName] = useState(user.u_name || user.name || '');
  const [avatarUrl, setAvatarUrl] = useState(user.u_image || user.image || '');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const resolvedAvatarUrl = useMemo(() => resolveUserAvatar(avatarUrl), [avatarUrl]);

  useEffect(() => {
    setDisplayName(user.u_name || user.name || '');
    setAvatarUrl(user.u_image || user.image || '');
  }, [user?.uid]);

  const handleNameChange = (e) => {
    const value = String(e.target.value).replace(/[^a-zA-Z\s]/g, '');
    setDisplayName(value);
    setError('');
  };

  const validateName = () => {
    const nameValue = String(displayName || '').trim();
    if (!nameValue || nameValue.length < 2) {
      setError('Name must be at least 2 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateName()) return;

    setSaving(true);
    try {
      await authApi.updateProfile({ name: displayName.trim() });
      showSuccess('Profile updated successfully');

      try {
        const storedSession = localStorage.getItem('session');
        if (storedSession) {
          const parsed = JSON.parse(storedSession);
          const nextSession = { ...parsed, u_name: displayName.trim(), name: displayName.trim() };
          localStorage.setItem('session', JSON.stringify(nextSession));
          window.dispatchEvent(new CustomEvent('session:updated', { detail: { session: nextSession } }));
        }
      } catch (err) {
        console.error('Failed to update stored session:', err);
      }
    } catch (err) {
      console.error('Failed to update profile', err);
      showError(err?.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!FILE_TYPES.IMAGE.includes(file.type)) {
      showError('Please upload an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showError('Image size must be under 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    setUploadingImage(true);

    try {
      const response = await authApi.uploadProfileImage(formData);
      const imagePath =
        response?.data?.imageUrl ||
        response?.data?.data?.imageUrl ||
        response?.data?.data?.image_path;
      if (imagePath) {
        setAvatarUrl(imagePath);
        try {
          const storedSession = localStorage.getItem('session');
          if (storedSession) {
            const parsed = JSON.parse(storedSession);
            const nextSession = { ...parsed, u_image: imagePath, image: imagePath };
            localStorage.setItem('session', JSON.stringify(nextSession));
            window.dispatchEvent(new CustomEvent('session:updated', { detail: { session: nextSession } }));
          }
        } catch (err) {
          console.error('Failed to update stored session image:', err);
        }
      }
      showSuccess('Profile image updated');
    } catch (err) {
      console.error('Failed to upload image', err);
      showError(err?.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 800,
        mx: 'auto',
        bgcolor: '#f8fafc',
        borderRadius: { xs: 0, md: 4 },
        minHeight: 'calc(100vh - 120px)',
      }}
    >
      {/* Header Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          p: { xs: 3, md: 4 },
          mb: 4,
          background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 55%, #4ade80 100%)',
          color: '#f8fafc',
          boxShadow: '0 18px 36px rgba(15, 23, 42, 0.18)',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
            }}
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Box flex={1}>
            <Chip
              label="Agent Profile"
              icon={<Sparkles size={14} />}
              sx={{
                bgcolor: 'rgba(255,255,255,0.22)',
                color: '#fff',
                mb: 1,
                fontWeight: 600,
              }}
            />
            <Typography variant="h5" fontWeight={700}>
              Profile Settings
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Update your display name and profile photo
            </Typography>
          </Box>
        </Stack>
      </Card>

      {/* Profile Photo Card */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          bgcolor: '#ffffff',
          boxShadow: '0 12px 24px rgba(15, 23, 42, 0.06)',
          mb: 3,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          alignItems={{ xs: 'center', sm: 'flex-start' }}
        >
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={resolvedAvatarUrl}
              alt={displayName || 'Agent'}
              sx={{
                width: 120,
                height: 120,
                border: '4px solid #fff',
                boxShadow: '0 8px 24px rgba(22, 163, 74, 0.2)',
              }}
            />
            <IconButton
              component="label"
              disabled={uploadingImage}
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                backgroundColor: '#16a34a',
                color: '#fff',
                width: 36,
                height: 36,
                '&:hover': { backgroundColor: '#15803d' },
                boxShadow: '0 4px 12px rgba(22, 163, 74, 0.4)',
              }}
            >
              <Camera size={18} />
              <input hidden type="file" accept="image/*" onChange={handleImageUpload} />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography variant="h6" fontWeight={700} color="text.primary" mb={0.5}>
              {displayName || 'Agent'}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent={{ xs: 'center', sm: 'flex-start' }}
              mb={2}
            >
              <Mail size={16} color="#64748b" />
              <Typography variant="body2" color="text.secondary">
                {user.u_email || user.email || '-'}
              </Typography>
            </Stack>
            <Button
              component="label"
              variant="outlined"
              size="small"
              disabled={uploadingImage}
              startIcon={<Upload size={16} />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                borderColor: '#16a34a',
                color: '#16a34a',
                '&:hover': {
                  borderColor: '#15803d',
                  backgroundColor: alpha('#16a34a', 0.04),
                },
              }}
            >
              {uploadingImage ? 'Uploading...' : 'Upload Photo'}
              <input hidden type="file" accept="image/*" onChange={handleImageUpload} />
            </Button>
            <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 1 }}>
              JPG, PNG or GIF. Max 2MB.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Edit Name Card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          bgcolor: '#ffffff',
          boxShadow: '0 12px 24px rgba(15, 23, 42, 0.06)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.08)',
            background: 'linear-gradient(135deg, rgba(22, 163, 74, 0.05) 0%, rgba(34, 197, 94, 0.02) 100%)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: alpha('#16a34a', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <User size={20} color="#16a34a" />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                Display Name
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This name will be visible to customers in live chat
              </Typography>
            </Box>
          </Stack>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Full Name"
              name="displayName"
              value={displayName}
              onChange={handleNameChange}
              error={Boolean(error)}
              helperText={error}
              fullWidth
              placeholder="Enter your full name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <User size={18} color="#94a3b8" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#16a34a',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#16a34a',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#16a34a',
                },
              }}
            />
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={<Check size={18} />}
                sx={{
                  backgroundColor: '#16a34a',
                  borderRadius: 2,
                  px: 4,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(22, 163, 74, 0.3)',
                  '&:hover': {
                    backgroundColor: '#15803d',
                    boxShadow: '0 6px 20px rgba(22, 163, 74, 0.4)',
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Paper>

      {/* Info Note */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mt: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: alpha('#16a34a', 0.2),
          bgcolor: alpha('#16a34a', 0.04),
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: alpha('#16a34a', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Sparkles size={16} color="#16a34a" />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600} color="text.primary" mb={0.5}>
              Profile Information
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Your display name is shown to customers during live chat conversations.
              Keep it professional and friendly. For other account changes, please contact your administrator.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default AgentProfilePage;
